import { useEffect, useMemo, useState } from 'react';

import { useGetDesignItemQuery, useLazyGetDesignItemQuery } from '@/api/DesignQuery';
import type { ICartItemDetail } from '@/common/interfaces/cart.interface';
import type { IDesignItem } from '@/common/interfaces/design.interface';
import {
  getDesignGroupProductOptionsFromDesignItem,
  getUniqueStoreCartProofItemIds,
  resolveStoreCartDesignGroupProductOptions,
  resolveStoreCartDesignItemProofItemId,
} from '@/modules/Cart/StoreCart/helpers/storeCartDesignOptions.utils';
import { getStoreCartItemKeys } from '@/modules/Cart/StoreCart/helpers/storeCartRemoval.utils';

interface IUseStoreCartDesignGroupProductOptionsParams {
  proofId: number;
  cartItems: ICartItemDetail[];
  fallbackProofItemId: number;
}

export const useStoreCartDesignGroupProductOptions = ({
  proofId,
  cartItems,
  fallbackProofItemId,
}: IUseStoreCartDesignGroupProductOptionsParams) => {
  const resolvedProofItemId = useMemo(
    () => resolveStoreCartDesignItemProofItemId(cartItems, fallbackProofItemId),
    [cartItems, fallbackProofItemId]
  );
  const proofItemIds = useMemo(
    () => getUniqueStoreCartProofItemIds(cartItems, fallbackProofItemId),
    [cartItems, fallbackProofItemId]
  );
  const cartItemsKey = useMemo(() => getStoreCartItemKeys(cartItems), [cartItems]);

  const { data: primaryDesignItemResponse } = useGetDesignItemQuery(
    {
      proofId,
      proofItemId: resolvedProofItemId,
    },
    {
      skip: !proofId || !resolvedProofItemId,
      // Always re-fetch when store cart mounts so newly added color/product options appear.
      refetchOnMountOrArgChange: true,
    }
  );

  const [fetchDesignItem] = useLazyGetDesignItemQuery();
  const [fallbackDesignItem, setFallbackDesignItem] = useState<IDesignItem | undefined>();

  const primaryDesignItem = useMemo(
    () => primaryDesignItemResponse?.proofItem?.[0] as IDesignItem | undefined,
    [primaryDesignItemResponse]
  );

  useEffect(() => {
    setFallbackDesignItem(undefined);
  }, [proofId, resolvedProofItemId, cartItemsKey]);

  useEffect(() => {
    if (!proofId || getDesignGroupProductOptionsFromDesignItem(primaryDesignItem).length) {
      return undefined;
    }

    let cancelled = false;

    const loadFallbackDesignItem = async () => {
      const alternateProofItemIds = proofItemIds.filter((id) => id !== resolvedProofItemId);

      const resolvedDesignItem = await alternateProofItemIds.reduce<Promise<IDesignItem | null>>(
        async (accPromise, proofItemId) => {
          const acc = await accPromise;
          if (acc) {
            return acc;
          }

          try {
            const response = await fetchDesignItem({ proofId, proofItemId }).unwrap();
            const designItem = response?.proofItem?.[0] as IDesignItem | undefined;

            if (getDesignGroupProductOptionsFromDesignItem(designItem).length) {
              return designItem ?? null;
            }
          } catch {
            /* try next proof item id */
          }

          return null;
        },
        Promise.resolve(null)
      );

      if (!cancelled && resolvedDesignItem) {
        setFallbackDesignItem(resolvedDesignItem);
      }
    };

    loadFallbackDesignItem().catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [fetchDesignItem, primaryDesignItem, proofId, proofItemIds, resolvedProofItemId]);

  const designItem = useMemo(() => {
    if (getDesignGroupProductOptionsFromDesignItem(primaryDesignItem).length) {
      return primaryDesignItem;
    }

    return fallbackDesignItem ?? primaryDesignItem;
  }, [fallbackDesignItem, primaryDesignItem]);

  const productOptions = useMemo(
    () => resolveStoreCartDesignGroupProductOptions(designItem, cartItems),
    [cartItems, designItem]
  );

  return {
    designItem,
    productOptions,
  };
};
